import { useEffect, forwardRef } from "react";
import { AllowedFileTypesEnum, ApplicationQuestion } from "@/api/lctApISchemas";
import { RefCallBack, ControllerRenderProps, FieldValues, useFormContext } from "react-hook-form";
import { FormControl, FormDescription, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { MultipleChoice } from "./components/MultipleChoice";
import { TextAreaQuestion } from "./components/TextArea";
import { UploadQuestion } from "./components/UploadQuestion";
import { Boolean } from "./components/Boolean";
import { Select } from "./components/Select";
import { DatePicker } from "./components/DatePicker";
import { formatDefaultValue } from "../PVPApplicationFormSection";
import { Tooltip } from "@/components/Tooltip";
import { QuestionMarkCircledIcon } from "@radix-ui/react-icons";
import { cn } from "@/lib/utils";

interface IProps {
  question: ApplicationQuestion;
  name: string;
  storedValue: string | Date | number | string[];
  isEditable?: boolean;
  ref?: RefCallBack;
  fieldsLocked?: boolean;
  userAppId?: string;
}

export interface BaseQuestionProps {
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  field: ControllerRenderProps<FieldValues, string>;
  className?: string;
}

export const PVPApplicationFormQuestion = forwardRef<HTMLDivElement, IProps>(
  ({ question, name, storedValue, isEditable = true, fieldsLocked, userAppId }, ref) => {
    const formContext = useFormContext();
    const {
      control,
      setValue,
      formState: { errors },
    } = formContext;

    const {
      id: questionId,
      type,
      question_text: label,
      question_subtext: subtext,
      required,
      answer_options: answerOptions,
      allowed_file_types: allowedFileTypes,
      helper_text: helperText,
      placeholder_text: placeholder,
    } = question;
    useEffect(() => {
      const formattedValue = formatDefaultValue(type, storedValue);
      setValue(name, formattedValue);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storedValue]);

    const renderInput = (field: ControllerRenderProps<FieldValues, string>) => {
      const { ...restField } = field;
      const hasError = errors[name];
      const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const newValue = e.target.value;
        field.onChange(newValue);
      };
      const errorClassName = hasError ? "border-errorLight outline-errorLight" : "";

      switch (type) {
        case "TA": {
          if (typeof storedValue !== "string") {
            return null;
          }
          return (
            <TextAreaQuestion
              field={restField}
              disabled={!isEditable || fieldsLocked}
              key={name}
              placeholder={placeholder}
              className={errorClassName}
            />
          );
        }
        case "TEXT": {
          if (typeof storedValue !== "string") {
            return null;
          }
          return (
            <Input
              placeholder={placeholder}
              {...restField}
              disabled={!isEditable || fieldsLocked}
              onChange={handleChange}
              className={cn(`sm:max-w-[50%] md:my-3 md:max-w-[70%]`, errorClassName)}
            />
          );
        }
        case "NUMBER":
        case "INTEGER": {
          if (typeof storedValue !== "string") {
            return null;
          }
          return (
            <Input
              onWheel={(e) => e.currentTarget.blur()} //disable number scrolling
              placeholder={placeholder}
              {...restField}
              type="number"
              disabled={!isEditable || fieldsLocked}
              onChange={handleChange}
              className={cn(`sm:max-w-[50%] md:my-3 md:max-w-[70%]`, errorClassName)}
              value={field.value || ""}
            />
          );
        }
        case "EMAIL": {
          if (typeof storedValue !== "string") {
            return null;
          }
          return (
            <Input
              placeholder={placeholder}
              {...restField}
              type="email"
              disabled={!isEditable || fieldsLocked}
              onChange={handleChange}
              className={cn(`sm:max-w-[50%] md:my-3 md:max-w-[70%]`, errorClassName)}
            />
          );
        }
        case "PHONE":
          return (
            <Input
              placeholder={placeholder}
              pattern="\+?[0-9]*"
              {...restField}
              type="tel"
              value={field.value || ""}
              disabled={!isEditable || fieldsLocked}
              onChange={(e) => {
                const rawValue = e.target.value.replace(/[^0-9+]/g, "");
                field.onChange(rawValue);
                setValue(name, rawValue);
              }}
              className={cn(`sm:max-w-[50%] md:my-3 md:max-w-[70%]`, errorClassName)}
            />
          );
        case "BOOLEAN": {
          if (typeof storedValue !== "string") {
            return null;
          }
          return <Boolean disabled={!isEditable || fieldsLocked} field={field} className={errorClassName} />;
        }
        case "MC": {
          const options =
            answerOptions?.map((option) => ({
              id: option.id,
              label: option.option_text,
            })) || [];

          return <MultipleChoice field={restField} disabled={!isEditable || fieldsLocked} options={options} className={errorClassName} />;
        }
        case "SELECT": {
          const options =
            answerOptions?.map((option) => ({
              value: option.id,
              label: option.option_text,
            })) || [];
          return (
            <Select
              field={field}
              {...restField}
              disabled={!isEditable || fieldsLocked}
              placeholder={placeholder || "Select an option"}
              options={options}
              className={errorClassName}
            />
          );
        }
        case "DT":
        case "DATE": {
          return (
            <DatePicker
              field={field}
              disabled={!isEditable || fieldsLocked}
              placeholder={placeholder}
              storedValue={storedValue as Date}
              className={errorClassName}
            />
          );
        }
        case "UPLOAD": {
          if (!userAppId) return null;

          return (
            <UploadQuestion
              allowedFileTypes={allowedFileTypes as unknown as AllowedFileTypesEnum[]}
              value={field.value || ""}
              onChange={field.onChange}
              userAppId={userAppId}
              questionId={questionId}
              hasError={!!hasError}
            />
          );
        }
        default:
          return (
            <Input
              placeholder={placeholder}
              {...field}
              disabled={!isEditable || fieldsLocked}
              onChange={handleChange}
              className={`sm:max-w-[50%] md:my-3 md:max-w-[70%]`}
            />
          );
      }
    };

    if (!control || !type) {
      return null;
    }

    return (
      <FormField
        control={control}
        name={name}
        render={({ field }) => (
          <FormItem ref={ref} className={`my-2 flex flex-col px-2`}>
            <div className="flex flex-row items-center justify-start text-left align-middle">
              <div className="text-md mb-0 mt-2 text-black">
                {label}
                {required && <span className="ml-1 pb-2 text-sm italic text-error">* Required</span>}
              </div>
              {helperText ? (
                <div onClick={(e) => e.preventDefault()}>
                  <Tooltip label={helperText} side={"bottom"} className="max-w-[50vw]">
                    <QuestionMarkCircledIcon
                      className="ml-1 h-4 w-4 font-semibold text-gray-500 transition-colors hover:text-gray-700 active:text-gray-900"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    />
                  </Tooltip>
                </div>
              ) : null}
            </div>
            <FormControl>{renderInput(field)}</FormControl>
            {subtext ? <FormDescription>{subtext}</FormDescription> : null}
            <FormMessage />
          </FormItem>
        )}
      />
    );
  },
);

PVPApplicationFormQuestion.displayName = "PVPApplicationFormQuestion";
